import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import apiClient from "../../services/apiClient";
import "./ProblemBankPage.css";

/**
 * 문제뱅크: 뱅크에서 범위를 골라 시험지를 만드는 화면.
 *
 * 흐름은 문제은행형 서비스의 출제 화면을 따른다. 상단 종류 탭 → 왼쪽 넓은
 * 단원 트리(체크) → 오른쪽 설정 패널(문항 수·담은 요약·시험지 만들기).
 * 만들어진 시험지는 **출제 문제지 목록** 상단에 뜨고, 학생·마감일 선택(출제)도
 * 거기서 한다. 결과 보기는 그 목록에서 `?t=<토큰>` 으로 이 화면의 결과 뷰어를 연다.
 *
 * 정답·해설은 서버에만 있고, 이용권(problem_bank 애드온)·문법 상한 판정도
 * 전부 서버가 한다.
 */
const KIND_LIST = [
  { key: "grammar", name: "문법" },
  { key: "listening", name: "듣기" },
  { key: "vocab", name: "단어" },
  { key: "reading", name: "문해력" },
];
// 실제로 무엇을 여는지는 서버(`OPEN_KIND_LIST`)가 정한다. 듣기·문해력은 뱅크
// 정비가 끝날 때까지 잠겨 있어 탭 자체가 서지 않는다.
const openKindList = (options) =>
  KIND_LIST.filter((one) => (options?.kind_list || ["grammar", "vocab"]).includes(one.key));
const CIRCLED = ["①", "②", "③", "④", "⑤", "⑥"];

// 공용 apiClient 를 탄다. 생 fetch 는 액세스 토큰이 만료된 채 들어오면 401 로
// 끝나서 화면이 '불러오는 중' 에 멈췄다. apiClient 는 갱신 후 1회 재시도한다.
const bankFetch = (path, options = {}) => {
  const url = `/api/v1/echo/bank${path}`;
  const request = options.method === "POST"
    ? apiClient.post(url, options.body ? JSON.parse(options.body) : {}, { withAuth: true })
    : apiClient.get(url, { withAuth: true });
  return request
    .then((response) => response.data)
    .catch((error) => error.response?.data || { ok: false, error: "연결에 실패했어요" });
};

const Check = ({ state }) => (
  <span
    className={`problem-bank-check${state === "on" ? " problem-bank-check--on" : state === "half" ? " problem-bank-check--half" : ""}`}
  >
    {state !== "off" ? "✓" : ""}
  </span>
);

const ProblemBankPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [options, setOptions] = useState(null);
  const [kind, setKind] = useState("grammar");
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resultView, setResultView] = useState(null);

  const [grammarLevelId, setGrammarLevelId] = useState(null);
  const [openCategoryIds, setOpenCategoryIds] = useState([]);
  const [grammarSubIds, setGrammarSubIds] = useState([]);
  // 소분류를 하나씩 누르기 번거로워서, 누른 채로 훑으면 이어서 선택되게 한다.
  // 처음 누른 칸이 꺼져 있었으면 훑는 동안 켜고, 켜져 있었으면 끈다.
  const dragModeRef = useRef(null);
  // 대분류는 행을 누르면 펼치기도 해야 해서, 누른 즉시 고르지 않고
  // 옆 행으로 넘어가는 순간부터 드래그로 본다.
  const pendingCategoryRef = useRef(null);
  const dragMovedRef = useRef(false);
  const stopDrag = () => {
    dragModeRef.current = null;
    pendingCategoryRef.current = null;
  };
  useEffect(() => {
    window.addEventListener("mouseup", stopDrag);
    window.addEventListener("touchend", stopDrag);
    return () => {
      window.removeEventListener("mouseup", stopDrag);
      window.removeEventListener("touchend", stopDrag);
    };
  }, []);

  // 대분류를 훑으면 그 아래 소분류를 통째로 켜고 끈다.
  const applyDragCategory = (category, turnOn) => {
    const ids = category.sub_items.map((sub) => sub.sub_id);
    setGrammarSubIds((list) => {
      if (turnOn) {
        const missing = ids.filter((id) => !list.includes(id));
        return missing.length ? [...list, ...missing] : list;
      }
      return list.filter((one) => !ids.includes(one));
    });
  };

  const applyDrag = (subId, turnOn) => {
    setGrammarSubIds((list) => {
      const has = list.includes(subId);
      if (turnOn && !has) return [...list, subId];
      if (!turnOn && has) return list.filter((one) => one !== subId);
      return list;
    });
  };
  const [grammarPerSub, setGrammarPerSub] = useState(2);
  // 문항 형식 (문법·문해력): mc 객관식 / short 주관식. 최소 하나는 남긴다.
  const [answerTypes, setAnswerTypes] = useState(["mc", "short", "essay"]);
  const [vocabBands, setVocabBands] = useState([]);
  // 제목은 교사가 적는다. 목록에서 무엇을 낸 시험지인지 알아볼 이름이 필요하다.
  const [title, setTitle] = useState("");
  // 만드는 방식. draw = 범위를 골라 그때그때 뽑는다 / round = 미리 짜 둔 레벨 세트를
  // 그대로 낸다 / unit_set = 커리큘럼 단원의 고정 Set 을 그대로 낸다.
  const [mode, setMode] = useState("draw");
  const [roundLevel, setRoundLevel] = useState(null);
  // 회차는 여러 개를 같이 골라 한 시험지로 낼 수 있다 (1·2회차 = 100단어)
  const [roundNumbers, setRoundNumbers] = useState([]);
  const [curriculumLevel, setCurriculumLevel] = useState(null);
  const [curriculumUnitId, setCurriculumUnitId] = useState(null);
  // 단원마다 고른 Set 번호. {단원id: [1, 2]}. 여러 단원을 한 시험지로 합칠 수 있다.
  const [curriculumPicks, setCurriculumPicks] = useState({});
  // Set 여러 개를 같이 골라 한 시험지로 합칠 수 있다 (Set 1·2 = 40문항)
  const [readingLevel, setReadingLevel] = useState(null);
  const [readingAreas, setReadingAreas] = useState([]);
  const [readingSetCount, setReadingSetCount] = useState(1);
  const [listenStages, setListenStages] = useState([]);
  const [listenCount, setListenCount] = useState(10);
  // 듣기 유형(담화 목적·심정·태도 같은 것). 비우면 전 유형에서 뽑는다.
  const [listenQtypes, setListenQtypes] = useState([]);
  // 문법 트리처럼 스테이지를 펼쳐 그 안의 유형을 고른다
  const [openListenStages, setOpenListenStages] = useState([]);

  useEffect(() => {
    bankFetch("/options/").then((response) => {
      if (response.ok) {
        setOptions(response);
        // 열린 종류만 탭이 선다(문해력은 지인학원만, 수학 계정은 문해력만). 처음 탭이 닫혀 있으면 열린 첫 탭으로
        const firstOpenKind = KIND_LIST.find((one) => (response.kind_list || []).includes(one.key));
        if (firstOpenKind) setKind((current) => ((response.kind_list || []).includes(current) ? current : firstOpenKind.key));
        if (response.grammar_tree.length > 0) {
          setGrammarLevelId(response.grammar_tree[0].level_id);
        }
      }
    });
  }, []);

  // 출제 문제지 목록에서 결과 보기로 들어온 경우
  useEffect(() => {
    const token = searchParams.get("t");
    if (!token) return;
    bankFetch(`/result/?t=${token}`).then((response) => {
      if (response.ok) setResultView(response);
    });
  }, [searchParams]);

  const grammarLevel = useMemo(
    () => options?.grammar_tree.find((level) => level.level_id === grammarLevelId),
    [options, grammarLevelId]
  );
  const subLabelOf = useMemo(() => {
    const map = {};
    options?.grammar_tree.forEach((level) =>
      level.categories.forEach((category) =>
        category.sub_items.forEach((sub) => {
          map[sub.sub_id] = sub.sub_label;
        })
      )
    );
    return map;
  }, [options]);

  const toggleIn = (list, value, setList) =>
    setList(list.includes(value) ? list.filter((one) => one !== value) : [...list, value]);

  const categoryState = (category) => {
    const ids = category.sub_items.map((sub) => sub.sub_id);
    const pickedCount = ids.filter((id) => grammarSubIds.includes(id)).length;
    if (pickedCount === 0) return "off";
    return pickedCount === ids.length ? "on" : "half";
  };
  const toggleCategory = (category) => {
    const ids = category.sub_items.map((sub) => sub.sub_id);
    const allPicked = ids.every((id) => grammarSubIds.includes(id));
    setGrammarSubIds((list) =>
      allPicked ? list.filter((id) => !ids.includes(id)) : [...new Set([...list, ...ids])]
    );
  };

  // 담은 범위 요약 (오른쪽 패널)
  const picked = useMemo(() => {
    if (kind === "grammar")
      return grammarSubIds.map((subId) => ({
        key: subId,
        label: subLabelOf[subId] || subId,
        remove: () => setGrammarSubIds((list) => list.filter((one) => one !== subId)),
      }));
    if (kind === "vocab")
      return vocabBands.map((band) => ({
        key: band,
        label: options?.vocab_bands.find((one) => one.band === band)?.name || band,
        remove: () => setVocabBands((list) => list.filter((one) => one !== band)),
      }));
    if (kind === "reading") {
      const rows = readingLevel
        ? [{ key: "level", label: `${readingLevel} 레벨`, remove: () => setReadingLevel(null) }]
        : [];
      return rows.concat(
        readingAreas.map((area) => ({
          key: area,
          label: area,
          remove: () => setReadingAreas((list) => list.filter((one) => one !== area)),
        }))
      );
    }
    return listenStages.map((stage) => ({
      key: stage,
      label: `STAGE ${String(stage).padStart(2, "0")}`,
      remove: () => setListenStages((list) => list.filter((one) => one !== stage)),
    }));
  }, [kind, grammarSubIds, subLabelOf, vocabBands, options, readingLevel, readingAreas, listenStages]);

  // 이 종류에 레벨 세트가 있는가. 서버는 종류별 레벨과 회차 수만 내려 준다(문항 수는 비밀).
  const roundLevelList = (options?.level_rounds || {})[kind] || [];
  const roundLevelRow = roundLevelList.find((one) => one.level === roundLevel);
  const canUseRound =
    (options?.round_kind_list || []).includes(kind) && roundLevelList.length > 0;

  const roundTotalCount = (() => {
    const detailList = roundLevelRow?.rounds_detail || [];
    if (detailList.length === 0) {
      return roundNumbers.length * (options?.round_item_count?.[kind] || 0);
    }
    return roundNumbers.reduce((sum, number) => {
      const detail = detailList.find((one) => one.number === number);
      if (!detail) return sum;
      if (kind !== "reading") return sum + detail.item_count;
      let count = 0;
      if (answerTypes.includes("mc")) count += detail.mc_count;
      if (answerTypes.includes("short")) count += detail.short_count;
      return sum + count;
    }, 0);
  })();

  const totalCount =
    mode === "round"
      ? roundTotalCount
      : kind === "grammar"
      ? Math.min(grammarSubIds.length * grammarPerSub, options?.grammar_set_max || 100)
      : kind === "vocab"
      ? `밴드 ${vocabBands.length}`
      : kind === "reading"
      ? `세트 ${readingSetCount}`
      : listenCount;

  // 단어는 '범위 골라 뽑기' 가 레벨 세트 전체 선택과 겹쳐서 레벨 세트만 쓴다.
  const canUseDraw = kind !== "vocab";

  // 커리큘럼 단원. 서버가 공개 조건을 만족한 단원과 열린 Set 번호만 내려 준다.
  const curriculumUnitList = (options?.curriculum || {})[kind] || [];
  // 고른 스테이지들이 가진 유형을 합친 목록. 스테이지를 고르기 전에는 비어 있다.
  // 범위 뽑기에서는 고른 스테이지들, 레벨 세트(회차)에서는 고른 레벨의 유형을 모은다.
  const listenQtypeStages =
    mode === "round"
      ? [Number(String(roundLevel || "").replace(/[^0-9]/g, ""))].filter(Boolean)
      : listenStages;
  const listenQtypeList = [
    ...new Set(
      (options?.listen_stages || [])
        .filter((one) => listenQtypeStages.includes(one.stage))
        .flatMap((one) => one.qtypes || [])
    ),
  ];

  const curriculumUnit = curriculumUnitList.find((one) => one.unit_id === curriculumUnitId);
  // 고른 단원들(왼쪽에서 고른 순서가 아니라 목록 순서대로 보여 준다)
  const pickedUnitList = curriculumUnitList.filter((one) => curriculumPicks[one.unit_id]);
  const curriculumUnitPayload = pickedUnitList
    .map((one) => ({
      unit_id: one.unit_id,
      set_numbers: [...(curriculumPicks[one.unit_id] || [])].sort((a, b) => a - b),
    }))
    .filter((one) => one.set_numbers.length > 0);
  const canUseCurriculum = curriculumUnitList.length > 0;
  // 범주(레벨) 목록. 서버가 레벨 순서대로 내려주므로 처음 나온 순서를 지킨다.
  const curriculumLevelList = curriculumUnitList.reduce((list, one) => {
    if (!list.some((row) => row.level === one.level)) {
      list.push({ level: one.level, level_name: one.level_name });
    }
    return list;
  }, []);

  // 종류를 바꿨는데 그 종류에 없는 방식이 남아 있으면 화면이 비어 버린다.
  useEffect(() => {
    if ((mode === "unit_set" && !canUseCurriculum) || (mode === "round" && !canUseRound)) {
      setMode(canUseDraw ? "draw" : "round");
    }
    if (mode === "draw" && !canUseDraw) {
      setMode("round");
    }
  }, [mode, canUseCurriculum, canUseRound, canUseDraw]);

  // 커리큘럼 범주가 비어 있거나 이 종류에 없는 레벨이면 첫 범주를 고른다.
  useEffect(() => {
    if (mode !== "unit_set" || curriculumLevelList.length === 0) return;
    if (!curriculumLevelList.some((one) => one.level === curriculumLevel)) {
      setCurriculumLevel(curriculumLevelList[0].level);
      setCurriculumUnitId(null);
      setCurriculumPicks({});
    }
  }, [mode, kind, curriculumLevel, curriculumLevelList]);

  const canCreate =
    mode === "unit_set"
      ? curriculumUnitPayload.length > 0
      : (mode === "round"
          ? Boolean(roundLevel && roundNumbers.length > 0)
          : (kind === "grammar" && grammarSubIds.length > 0) ||
          (kind === "vocab" && vocabBands.length > 0) ||
          (kind === "reading" && readingLevel) ||
          (kind === "listening" && listenStages.length > 0));

  const create = () => {
    const config =
      mode === "unit_set"
        ? { mode: "unit_set",
            units: curriculumUnitPayload,
            unit_id: curriculumUnitPayload[0]?.unit_id,
            set_numbers: curriculumUnitPayload[0]?.set_numbers || [],
            ...(kind === "grammar" || kind === "reading"
              ? { answer_types: answerTypes } : {}) }
        : mode === "round"
        ? { mode: "round", level: roundLevel, rounds: [...roundNumbers].sort((a, b) => a - b),
            ...(kind === "reading" ? { answer_types: answerTypes.filter((one) => one !== "essay") } : {}),
            ...(kind === "listening" ? { qtypes: listenQtypes } : {}) }
        : kind === "grammar"
        ? { sub_ids: grammarSubIds, per_sub: grammarPerSub, answer_types: answerTypes }
        : kind === "vocab"
        ? { bands: vocabBands }
        : kind === "reading"
        ? { levels: readingLevel ? [readingLevel] : [], areas: readingAreas,
            set_count: readingSetCount, answer_types: answerTypes }
        : { stages: listenStages, count: listenCount, qtypes: listenQtypes };
    setBusy(true);
    setMessage(null);
    bankFetch("/sets/", {
      method: "POST",
      body: JSON.stringify({
        kind,
        title: title.trim(),
        config,
      }),
    })
      .then((response) => {
        setBusy(false);
        if (!response.ok) {
          setMessage({ ok: false, text: response.error || "만들지 못했어요" });
          return;
        }
        setMessage({
          ok: true,
          text: `${response.item_count}문항 시험지를 만들었어요. 문제지 목록 맨 위에 생겼어요. 거기서 학생과 마감일을 골라 출제하세요.`,
        });
      })
      .catch(() => {
        setBusy(false);
        setMessage({ ok: false, text: "연결 오류. 다시 시도해 주세요." });
      });
  };

  const closeResult = () => {
    setResultView(null);
    if (searchParams.get("t")) setSearchParams({}, { replace: true });
  };

  if (!options) {
    return <div className="problem-bank"><div className="problem-bank-empty">문제뱅크를 불러오는 중…</div></div>;
  }
  if ((options.kind_list || []).length === 0) {
    return <p style={{ padding: "60px 0", textAlign: "center", color: "#9AA3AB" }}>아직 없어요.</p>;
  }

  const numberButtons = (values, current, setter) => (
    <div className="problem-bank-num-row">
      {values.map((value) => (
        <button
          key={value}
          type="button"
          className={`problem-bank-num${current === value ? " problem-bank-num--on" : ""}`}
          onClick={() => setter(value)}
        >
          {value}
        </button>
      ))}
      <input
        type="number"
        min="1"
        className="problem-bank-num-input"
        value={current}
        onChange={(event) => setter(Number(event.target.value))}
      />
    </div>
  );

  return (
    <div className="problem-bank">
      <div className="problem-bank-header">
        <span className="problem-bank-step-label">STEP 1</span>
        <span className="problem-bank-title">시험지 종류 및 범위 선택</span>
        <div className="problem-bank-steps">
          <span><span className="problem-bank-step-dot problem-bank-step-dot--on">1</span><b>범위 선택</b></span>
          <span><span className="problem-bank-step-dot">2</span>출제는 출제 문제지 목록에서</span>
        </div>
      </div>

      {options && (
        <div
          style={{
            display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap",
            margin: "0 0 10px", padding: "10px 16px",
            background: "#EFFBF8", border: "1px solid #CFF2EC",
            borderRadius: "12px", fontSize: "13px", color: "#0E141B",
          }}
        >
          <span style={{ fontWeight: 800, color: "#00A396" }}>✨ special</span>
          <span style={{ color: "#3A434E" }}>
            {options.addon_active
              ? "직독직해+ 옵션 이용 중이에요. 문항 수 제한 없이 출제할 수 있어요."
              : `직독직해+ 옵션 가입 학원만 실제 학생에게 출제할 수 있어요. 지금은 체험 출제로 한 달에 ${options.demo_monthly_max || 100}문항까지 만들어 볼 수 있어요 (이번 달 ${options.demo_monthly_left ?? 100}문항 남음).`}
          </span>
        </div>
      )}
      <div className="problem-bank-tabs">
        {openKindList(options).map((one) => (
          <button
            key={one.key}
            type="button"
            className={`problem-bank-tab${kind === one.key ? " problem-bank-tab--on" : ""}`}
            onClick={() => { setKind(one.key); setMessage(null); }}
          >
            {one.name}
          </button>
        ))}
      </div>

      {(canUseRound || canUseCurriculum) && (
        <div className="problem-bank-mode-bar">
          {canUseDraw && (
          <button
            type="button"
            className={`problem-bank-mode${mode === "draw" ? " problem-bank-mode--on" : ""}`}
            onClick={() => { setMode("draw"); setMessage(null); }}
          >
            범위 골라 뽑기
            <small>고른 범위에서 그때그때 다르게 나가요</small>
          </button>
          )}
          {canUseRound && (
            <button
              type="button"
              className={`problem-bank-mode${mode === "round" ? " problem-bank-mode--on" : ""}`}
              onClick={() => { setMode("round"); setMessage(null); }}
            >
              {kind === "reading" ? "커리큘럼" : "레벨 세트"}
              <small>
                {kind === "reading"
                  ? "레벨과 Set 을 고르면 정해진 시험지가 그대로 나가요"
                  : "레벨과 회차를 고르면 정해진 시험지가 그대로 나가요"}
              </small>
            </button>
          )}
          {canUseCurriculum && (
            <button
              type="button"
              className={`problem-bank-mode${mode === "unit_set" ? " problem-bank-mode--on" : ""}`}
              onClick={() => { setMode("unit_set"); setMessage(null); }}
            >
              커리큘럼
              <small>단원과 Set 을 고르면 정해진 시험지가 그대로 나가요</small>
            </button>
          )}
        </div>
      )}

      <div className="problem-bank-body">
        {/* ── 왼쪽: 범위 트리 ── */}
        <div className="problem-bank-tree-area">
          {mode === "unit_set" && (
            <>
              <div className="problem-bank-tree-note">
                커리큘럼 순서대로 학습시킬 때 씁니다. 같은 단원을 한 번 학습시킨 뒤
                Set 1 → Set 2 순으로 추가 연습을 시킬 수 있어요. 같은 Set 은 언제 내도
                같은 문항이 나가고, Set 끼리는 문항이 겹치지 않아요.
              </div>
              {/* 1단계: 범주(레벨)를 고른다. 문법 뽑기 화면의 레벨 알약과 같은 모양 */}
              <div className="problem-bank-level-bar">
                {curriculumLevelList.map((one) => (
                  <button
                    key={one.level}
                    type="button"
                    className={`problem-bank-level-pill${curriculumLevel === one.level ? " problem-bank-level-pill--on" : ""}`}
                    onClick={() => {
                      setCurriculumLevel(one.level);
                      setCurriculumUnitId(null);
                      setCurriculumPicks({});
                    }}
                  >
                    {one.level_name}
                  </button>
                ))}
              </div>
              {/* 2단계: 그 레벨의 단원 목록. 문법은 대분류(수동태 등)를 소제목으로 끊는다 */}
              {(() => {
                const levelUnitList = curriculumUnitList.filter((one) => one.level === curriculumLevel);
                return levelUnitList.map((one, index) => (
                  <div key={one.unit_id}>
                    {one.group_name &&
                      (index === 0 || levelUnitList[index - 1].group_name !== one.group_name) && (
                        <div className="problem-bank-tree-note" style={{ fontWeight: 800, color: "#0E141B", paddingTop: "12px" }}>
                          {one.group_name}
                        </div>
                      )}
                    <button
                      type="button"
                      className="problem-bank-tree-row"
                      onClick={() => {
                        setCurriculumUnitId(one.unit_id);
                        setCurriculumPicks((picks) => {
                          const next = { ...picks };
                          if (next[one.unit_id]) delete next[one.unit_id];
                          else next[one.unit_id] = [];
                          return next;
                        });
                      }}
                    >
                      <Check state={curriculumPicks[one.unit_id] ? "on" : "off"} />
                      <span className="problem-bank-tree-label">
                        Unit {String(one.ordering).padStart(2, "0")}. {one.name}
                      </span>
                    </button>
                  </div>
                ));
              })()}
            </>
          )}
          {mode === "round" && (
            <>
              <div className="problem-bank-tree-note">
                반 전체에 같은 시험지를 낼 때 씁니다. 같은 회차는 언제 내도 같은 문항이 나가고,
                회차끼리는 문항이 겹치지 않아요.
              </div>
              {roundLevelList.map((one) => {
                const chosen = roundLevel === one.level;
                const stageNumber = Number(String(one.level).replace(/[^0-9]/g, ""));
                const stageRow = (options?.listen_stages || []).find((row) => row.stage === stageNumber);
                return (
                  <div key={one.level}>
                    <button
                      type="button"
                      className={`problem-bank-tree-row${kind === "listening" ? " problem-bank-tree-row--head" : ""}`}
                      onClick={() => {
                        setRoundLevel(one.level);
                        setRoundNumbers([]);
                        setListenQtypes([]);
                      }}
                    >
                      <Check state={chosen ? "on" : "off"} />
                      <span className="problem-bank-tree-label">{one.level_name}</span>
                      {kind === "listening" && stageRow && (
                        <span className="problem-bank-tree-count">
                          {(stageRow.qtypes || []).length}유형
                        </span>
                      )}
                    </button>
                    {kind === "listening" && chosen &&
                      (stageRow?.qtypes || []).map((qtype) => {
                        const picked = listenQtypes.includes(qtype);
                        return (
                          <button
                            key={qtype}
                            type="button"
                            className="problem-bank-tree-row problem-bank-tree-row--child"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              dragModeRef.current = !picked;
                              dragMovedRef.current = true;
                              setListenQtypes((list) =>
                                picked ? list.filter((item) => item !== qtype) : [...list, qtype]);
                            }}
                            onMouseEnter={() => {
                              if (dragModeRef.current === null) return;
                              const turnOn = dragModeRef.current;
                              setListenQtypes((list) => {
                                const has = list.includes(qtype);
                                if (turnOn && !has) return [...list, qtype];
                                if (!turnOn && has) return list.filter((item) => item !== qtype);
                                return list;
                              });
                            }}
                            onClick={(event) => event.preventDefault()}
                          >
                            <Check state={picked ? "on" : "off"} />
                            <span className="problem-bank-tree-label">{qtype}</span>
                          </button>
                        );
                      })}
                  </div>
                );
              })}
              {kind === "listening" && (
                <div className="problem-bank-tree-note">
                  유형을 고르지 않으면 그 회차 전체가 나갑니다. 고르면 그 유형만 나가고 문항 수가 줄어요.
                </div>
              )}
            </>
          )}
          {mode === "draw" && kind === "grammar" && (
            <>
              <div className="problem-bank-level-bar">
                {options.grammar_tree.map((level) => (
                  <button
                    key={level.level_id}
                    type="button"
                    className={`problem-bank-level-pill${grammarLevelId === level.level_id ? " problem-bank-level-pill--on" : ""}`}
                    onClick={() => setGrammarLevelId(level.level_id)}
                  >
                    {level.level_label}
                  </button>
                ))}
              </div>
              {grammarLevel?.categories.map((category) => {
                const open = openCategoryIds.includes(category.category_id);
                return (
                  <div key={category.category_id}>
                    <div style={{ display: "flex" }}>
                      <button
                        type="button"
                        className="problem-bank-tree-row problem-bank-tree-row--head"
                        onMouseDown={() => {
                          // 아직 고르지 않는다. 옆 행으로 끌고 가면 그때 드래그가 시작된다.
                          pendingCategoryRef.current = {
                            category,
                            turnOn: categoryState(category) !== "on",
                          };
                          dragMovedRef.current = false;
                        }}
                        onMouseEnter={() => {
                          const pending = pendingCategoryRef.current;
                          if (pending && dragModeRef.current === null) {
                            // 누른 행에서 옆 행으로 넘어온 순간: 두 행을 함께 켠다
                            dragModeRef.current = pending.turnOn;
                            dragMovedRef.current = true;
                            applyDragCategory(pending.category, pending.turnOn);
                          }
                          if (dragModeRef.current === null) return;
                          applyDragCategory(category, dragModeRef.current);
                        }}
                        onClick={() => {
                          // 끌고 온 손이 놓인 것이면 펼치지 않는다
                          if (dragMovedRef.current) {
                            dragMovedRef.current = false;
                            return;
                          }
                          toggleIn(openCategoryIds, category.category_id, setOpenCategoryIds);
                        }}
                      >
                        <span className="problem-bank-caret">{open ? "▼" : "▶"}</span>
                        <span
                          onMouseDown={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            const turnOn = categoryState(category) !== "on";
                            dragModeRef.current = turnOn;
                            dragMovedRef.current = true;
                            applyDragCategory(category, turnOn);
                          }}
                          onMouseEnter={() => {
                            if (dragModeRef.current === null) return;
                            applyDragCategory(category, dragModeRef.current);
                          }}
                          onClick={(event) => { event.stopPropagation(); event.preventDefault(); }}
                        >
                          <Check state={categoryState(category)} />
                        </span>
                        <span className="problem-bank-tree-label">{category.category_label}</span>
                        <span className="problem-bank-tree-count">{category.sub_items.length}단원</span>
                      </button>
                    </div>
                    {open &&
                      category.sub_items.map((sub) => {
                        const on = grammarSubIds.includes(sub.sub_id);
                        return (
                          <button
                            key={sub.sub_id}
                            type="button"
                            className="problem-bank-tree-row problem-bank-tree-row--child"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              dragModeRef.current = !on;
                              dragMovedRef.current = true;
                              applyDrag(sub.sub_id, !on);
                            }}
                            onMouseEnter={() => {
                              if (dragModeRef.current === null) return;
                              applyDrag(sub.sub_id, dragModeRef.current);
                            }}
                            onClick={(event) => event.preventDefault()}
                          >
                            <Check state={on ? "on" : "off"} />
                            <span className="problem-bank-tree-label">{sub.sub_label}</span>
                          </button>
                        );
                      })}
                  </div>
                );
              })}
            </>
          )}

          {mode === "draw" && kind === "listening" && (
            <>
              {options.listen_stages.map((stage) => {
                const on = listenStages.includes(stage.stage);
                const open = openListenStages.includes(stage.stage);
                return (
                  <div key={stage.stage}>
                    <div style={{ display: "flex" }}>
                      <button
                        type="button"
                        className="problem-bank-tree-row problem-bank-tree-row--head"
                        onClick={() => toggleIn(openListenStages, stage.stage, setOpenListenStages)}
                      >
                        <span className="problem-bank-caret">{open ? "▼" : "▶"}</span>
                        <span
                          onMouseDown={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            toggleIn(listenStages, stage.stage, setListenStages);
                          }}
                          onClick={(event) => { event.stopPropagation(); event.preventDefault(); }}
                        >
                          <Check state={on ? "on" : "off"} />
                        </span>
                        <span className="problem-bank-tree-label">
                          STAGE {String(stage.stage).padStart(2, "0")}
                        </span>
                        <span className="problem-bank-tree-count">{(stage.qtypes || []).length}유형</span>
                      </button>
                    </div>
                    {open &&
                      (stage.qtypes || []).map((qtype) => {
                        const picked = listenQtypes.includes(qtype);
                        return (
                          <button
                            key={qtype}
                            type="button"
                            className="problem-bank-tree-row problem-bank-tree-row--child"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              // 유형을 고르면 그 스테이지도 함께 담는다
                              if (!listenStages.includes(stage.stage)) {
                                setListenStages((list) => [...list, stage.stage]);
                              }
                              dragModeRef.current = !picked;
                              dragMovedRef.current = true;
                              setListenQtypes((list) =>
                                picked ? list.filter((one) => one !== qtype) : [...list, qtype]);
                            }}
                            onMouseEnter={() => {
                              if (dragModeRef.current === null) return;
                              const turnOn = dragModeRef.current;
                              setListenQtypes((list) => {
                                const has = list.includes(qtype);
                                if (turnOn && !has) return [...list, qtype];
                                if (!turnOn && has) return list.filter((one) => one !== qtype);
                                return list;
                              });
                            }}
                            onClick={(event) => event.preventDefault()}
                          >
                            <Check state={picked ? "on" : "off"} />
                            <span className="problem-bank-tree-label">{qtype}</span>
                          </button>
                        );
                      })}
                  </div>
                );
              })}
              <div className="problem-bank-tree-note">
                유형을 고르지 않으면 그 스테이지 전체에서 나갑니다. 고른 유형은 담은 스테이지 전체에 적용돼요.
              </div>
              <div className="problem-bank-tree-note">
                레벨체크 뱅크에서 나갑니다. 월간 테스트 문항은 시험이라 문제뱅크로 나가지 않아요.
              </div>
            </>
          )}

          {mode === "draw" && kind === "vocab" &&
            options.vocab_bands.map((band) => {
              const on = vocabBands.includes(band.band);
              return (
                <button
                  key={band.band}
                  type="button"
                  className="problem-bank-tree-row"
                  onClick={() => toggleIn(vocabBands, band.band, setVocabBands)}
                >
                  <Check state={on ? "on" : "off"} />
                  <span className="problem-bank-tree-label">{band.name}</span>
                </button>
              );
            })}
          {mode === "draw" && kind === "vocab" && (
            <div className="problem-bank-tree-note">
              여러 밴드를 고르면 합쳐서 한 시험지로 나가요. 고른 밴드는 단어 전체가
              4지선다로 나가요 (아이가 그 밴드를 아는지 확인하는 용도).
            </div>
          )}

          {mode === "draw" && kind === "reading" && (
            <>
              <div className="problem-bank-level-bar">
                {options.reading_levels.map((level) => (
                  <button
                    key={level.level}
                    type="button"
                    className={`problem-bank-level-pill${readingLevel === level.level ? " problem-bank-level-pill--on" : ""}`}
                    onClick={() => setReadingLevel(readingLevel === level.level ? null : level.level)}
                  >
                    {level.level}
                  </button>
                ))}
              </div>
              {readingLevel ? (
                (options.reading_levels.find((level) => level.level === readingLevel)?.areas || []).map((area) => {
                  const on = readingAreas.includes(area);
                  return (
                    <button
                      key={area}
                      type="button"
                      className="problem-bank-tree-row"
                      onClick={() => toggleIn(readingAreas, area, setReadingAreas)}
                    >
                      <Check state={on ? "on" : "off"} />
                      <span className="problem-bank-tree-label">{area}</span>
                    </button>
                  );
                })
              ) : (
                <div className="problem-bank-empty">위에서 KR 레벨을 골라 주세요.</div>
              )}
              {readingLevel && (
                <div className="problem-bank-tree-note">영역을 안 고르면 전체 영역에서 나가요.</div>
              )}
            </>
          )}
        </div>

        {/* ── 오른쪽: 설정 패널 ── */}
        <div className="problem-bank-config">
          {(kind === "grammar" || kind === "reading") && (mode !== "round" || kind === "reading") && (
            <div className="problem-bank-config-section">
              <div className="problem-bank-config-label">
                문항 형식 <small>고른 형식만 나가요 (뺀 만큼 문항 수가 줄어요)</small>
              </div>
              <div className="problem-bank-num-row">
                {(kind === "grammar"
                  ? [["mc", "객관식"], ["short", "주관식"], ["essay", "서술형"]]
                  : [["mc", "객관식"], ["short", "주관식"]]
                ).map(([key, name]) => (
                  <button
                    key={key}
                    type="button"
                    className={`problem-bank-num${answerTypes.includes(key) ? " problem-bank-num--on" : ""}`}
                    style={{ width: "76px" }}
                    onClick={() => {
                      if (answerTypes.includes(key)) {
                        if (answerTypes.length === 1) return;   // 최소 하나는 남긴다
                        setAnswerTypes(answerTypes.filter((one) => one !== key));
                      } else {
                        setAnswerTypes([...answerTypes, key]);
                      }
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {mode === "draw" && kind === "grammar" && (
            <div className="problem-bank-config-section">
              <div className="problem-bank-config-label">
                소분류당 문항 수 <small>시험지 최대 {options.grammar_set_max}문항{options.addon_active ? "" : ` · 체험 한도 이번 달 ${options.demo_monthly_left ?? options.daily_left}문항`}</small>
              </div>
              {numberButtons([1, 2, 3, 5], grammarPerSub, setGrammarPerSub)}
              <div className="problem-bank-picked-empty">
                담은 소분류마다 {grammarPerSub}문항씩 나가요.
              </div>
            </div>
          )}
          {mode === "draw" && kind === "listening" && (
            <>
              <div className="problem-bank-config-section">
                <div className="problem-bank-config-label">문제 수 <small>한 세트 최대 {options.listening_set_max}문항</small></div>
                {numberButtons([1, 5, 10, 15, 20], listenCount, setListenCount)}
              </div>
            </>
          )}
          {mode === "draw" && kind === "reading" && (
            <div className="problem-bank-config-section">
              <div className="problem-bank-config-label">
                지문 세트 수{" "}
                <small>
                  문항 상한: KR1~5는 {options.reading_item_max_low} · KR6부터 {options.reading_item_max_high}
                </small>
              </div>
              {numberButtons([1, 2, 3], readingSetCount, setReadingSetCount)}
            </div>
          )}

          {mode === "unit_set" && (
            <div className="problem-bank-config-section">
              <div className="problem-bank-config-label">
                Set <small>여러 개를 고르면 한 시험지로 합쳐져요</small>
              </div>
              {pickedUnitList.length === 0 ? (
                <div className="problem-bank-picked-empty">왼쪽에서 단원을 골라 주세요.</div>
              ) : (
                pickedUnitList.map((unit) => (
                  <div key={unit.unit_id} style={{ marginBottom: "10px" }}>
                    <div className="problem-bank-tree-note" style={{ padding: "2px 0 4px" }}>
                      Unit {String(unit.ordering).padStart(2, "0")}. {unit.name}
                    </div>
                    <div className="problem-bank-num-row">
                      {unit.set_numbers.map((number) => (
                        <button
                          key={number}
                          type="button"
                          className={`problem-bank-num${(curriculumPicks[unit.unit_id] || []).includes(number) ? " problem-bank-num--on" : ""}`}
                          onClick={() =>
                            setCurriculumPicks((picks) => {
                              const chosen = picks[unit.unit_id] || [];
                              const next = chosen.includes(number)
                                ? chosen.filter((one) => one !== number)
                                : [...chosen, number];
                              return { ...picks, [unit.unit_id]: next };
                            })
                          }
                        >
                          Set {number}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {mode === "round" && (
            <div className="problem-bank-config-section">
              <div className="problem-bank-config-label">
                {kind === "reading" ? "Set" : "회차"}
                {options.round_item_count?.[kind] ? (
                  <small>
                    {" "}한 회차 {options.round_item_count[kind]}
                    {kind === "vocab" ? "단어" : "문항"} · 여러 회차를 같이 낼 수 있어요
                  </small>
                ) : (
                  <small> 여러 개를 고르면 한 시험지로 합쳐져요</small>
                )}
              </div>
              {!roundLevel ? (
                <div className="problem-bank-picked-empty">왼쪽에서 레벨을 골라 주세요.</div>
              ) : (
                <div className="problem-bank-num-row">
                  <button
                    type="button"
                    className={`problem-bank-num${roundNumbers.length === (roundLevelRow?.rounds || 0) && roundNumbers.length > 0 ? " problem-bank-num--on" : ""}`}
                    onClick={() => {
                      const total = roundLevelRow?.rounds || 0;
                      setRoundNumbers(
                        roundNumbers.length === total
                          ? []
                          : Array.from({ length: total }, (_, index) => index + 1)
                      );
                    }}
                  >
                    전체
                  </button>
                  {Array.from({ length: roundLevelRow?.rounds || 0 }, (_, index) => index + 1).map(
                    (number) => {
                      const detail = (roundLevelRow?.rounds_detail || []).find(
                        (one) => one.number === number
                      );
                      const label = kind === "reading" ? `Set ${number}` : `${number}회차`;
                      return (
                        <button
                          key={number}
                          type="button"
                          className={`problem-bank-num${roundNumbers.includes(number) ? " problem-bank-num--on" : ""}`}
                          style={detail && kind === "reading" ? { width: "auto", padding: "8px 10px" } : undefined}
                          onClick={() => toggleIn(roundNumbers, number, setRoundNumbers)}
                        >
                          {label}
                          {detail && kind === "reading" && (
                            <small style={{ display: "block", fontWeight: 500 }}>{detail.item_count}문항</small>
                          )}
                        </button>
                      );
                    }
                  )}
                </div>
              )}
            </div>
          )}

          <div className="problem-bank-config-section">
            <div className="problem-bank-config-label">
              시험지 제목 <small>비우면 범위 이름으로 자동으로 붙어요</small>
            </div>
            <input
              type="text"
              value={title}
              maxLength={60}
              placeholder={
                mode === "unit_set"
                  ? curriculumUnitPayload.length > 0
                    ? pickedUnitList
                        .filter((one) => (curriculumPicks[one.unit_id] || []).length > 0)
                        .map((one) =>
                          `${one.name} Set ${[...(curriculumPicks[one.unit_id] || [])]
                            .sort((a, b) => a - b)
                            .join("·")}`)
                        .join(" + ")
                    : "단원을 고르면 자동으로 채워져요"
                  : "예) 3월 2주차 단어 시험 (비우면 자동)"
              }
              onChange={(event) => setTitle(event.target.value)}
              style={{
                width: "100%",
                boxSizing: "border-box",
                border: "1.5px solid #E3EBEA",
                borderRadius: "10px",
                padding: "9px 12px",
                fontSize: "14px",
                fontFamily: "inherit",
              }}
            />
          </div>

          {mode !== "unit_set" && (
          <div className="problem-bank-config-section">
            <div className="problem-bank-config-label">
              담은 범위 <small>{picked.length}개</small>
              {picked.length > 0 && (
                <button
                  type="button"
                  className="problem-bank-picked-clear"
                  onClick={() => picked.forEach((row) => row.remove())}
                >
                  모두 지우기
                </button>
              )}
            </div>
            {picked.length === 0 ? (
              <div className="problem-bank-picked-empty">왼쪽에서 범위를 골라 담아 주세요.</div>
            ) : (
              <div className="problem-bank-picked">
                {picked.map((row) => (
                  <div className="problem-bank-picked-row" key={row.key}>
                    <span className="problem-bank-picked-label">{row.label}</span>
                    <button type="button" className="problem-bank-picked-remove" onClick={row.remove}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          <div className="problem-bank-config-footer">
            <div className="problem-bank-total">
              {mode === "unit_set" ? (
                pickedUnitList.length === 0 ? (
                  <small>단원을 골라 주세요</small>
                ) : curriculumUnitPayload.length === 0 ? (
                  <small>Set 을 골라 주세요</small>
                ) : (
                  <>
                    단원 <b>{curriculumUnitPayload.length}</b>개 · Set{" "}
                    <b>{curriculumUnitPayload.reduce((sum, one) => sum + one.set_numbers.length, 0)}</b>개
                  </>
                )
              ) : (
                <>
                  시험지 문제 수 <b>{totalCount}</b>
                  <small>| 범위 {picked.length}개</small>
                </>
              )}
            </div>
            <button type="button" className="problem-bank-submit" disabled={busy || !canCreate} onClick={create}>
              {busy ? "만드는 중…" : "시험지 만들기 →"}
            </button>
            {message && (
              <div className={`problem-bank-message problem-bank-message--${message.ok ? "ok" : "error"}`}>
                {message.text}
              </div>
            )}
            {!options.addon_active && (
              <div className="problem-bank-notice">
                문제뱅크는 <b>직독직해+ 옵션</b>(구독관리에서 추가)으로 열리는 기능이에요.{" "}
                가입 전에는 체험 출제로 한 달에 {options.demo_monthly_max || 100}문항까지
                만들어 볼 수 있어요 (이번 달 {options.demo_monthly_left ?? 100}문항 남음).
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── 결과 뷰어 (출제 문제지 목록에서 넘어옴) ── */}
      {resultView && (
        <div className="problem-bank-result">
          <div className="problem-bank-result-head">
            <span className="problem-bank-result-title">
              {resultView.title} {resultView.student_name && `· ${resultView.student_name}`}
            </span>
            <button type="button" className="problem-bank-result-close" onClick={closeResult}>닫기 ✕</button>
          </div>
          {!resultView.submitted && (
            <div className="problem-bank-picked-empty">아직 제출 전이에요. 아래는 문제·정답·해설이에요.</div>
          )}
          {resultView.result && (
            <div className="problem-bank-result-score">
              {resultView.result.score}점 ({resultView.result.right}/{resultView.result.total})
            </div>
          )}
          {resultView.wrong_words.length > 0 && (
            <div className="problem-bank-wrong-words">
              <b>틀린 단어 {resultView.wrong_words.length}개</b><br />
              {resultView.wrong_words.map((word) => `${word.word} (${word.meaning})`).join(", ")}
            </div>
          )}
          {resultView.dictation && (
            <div style={{ fontSize: "13px" }}>
              딕테이션 복습: {(resultView.dictation.items || []).map((one) => `${one.right}/${one.total}`).join(" · ")}
            </div>
          )}
          <div className="problem-bank-result-list">
            {resultView.items.map((item, index) => {
              const detail = resultView.result?.detail?.[index];
              return (
                <div className="problem-bank-result-item" key={item.id}>
                  <span className={detail ? (detail.correct ? "problem-bank-result-item-mark--o" : "problem-bank-result-item-mark--x") : ""}>
                    {index + 1}. {detail ? (detail.correct ? "○" : "✕") : ""}
                  </span>
                  <span className="problem-bank-result-item-category">{item.category}</span>
                  <div style={{ whiteSpace: "pre-wrap" }}>{item.word || item.stem || item.q}</div>
                  <div className="problem-bank-result-item-answer">
                    정답 {CIRCLED[item.ans] || item.ans + 1} {item.opts?.[item.ans]}
                  </div>
                  {item.explain && <div>{item.explain}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProblemBankPage;
